import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

/**
 * Shared shell for the public legal pages (/terms, /privacy, /data-deletion).
 *
 * One layout so the three documents look and read alike, and so the footer
 * links to all of them from every one of them: Meta's app review and
 * reviewers of the other platforms each land on a single page, and from
 * there need to be able to reach the others.
 */

const LEGAL_LINKS = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/data-deletion", label: "Data deletion" },
] as const;

interface LegalLayoutProps {
  title: string;
  /** Shown under the title, e.g. "Last updated: October 22, 2026". */
  meta: ReactNode;
  children: ReactNode;
}

export function LegalLayout({ title, meta, children }: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <Link to="/" className="flex items-center">
            <img src="/full-logo.svg" alt="Spark" className="h-12" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:mb-6 [&_ul]:space-y-2 [&_li]:leading-relaxed [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2">
        <article className="prose prose-neutral dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {title}
          </h1>
          <p className="text-muted-foreground">{meta}</p>
          {children}
        </article>
      </main>

      <footer className="border-t border-border/50 py-6">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-4 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <p>
            &copy; {new Date().getFullYear()} Charisol. All rights reserved.
          </p>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-4 gap-y-2">
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="transition-colors hover:text-primary"
              >
                {link.label}
              </Link>
            ))}
            <a
              href="mailto:info@charisol.io"
              className="transition-colors hover:text-primary"
            >
              info@charisol.io
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
